/**
 * Carrier History API Service
 *
 * Fetch layer for the read-only carrier authority/insurance history endpoints:
 *   GET /api/carrier-history/authority/:dotNumber
 *   GET /api/carrier-history/insurance/:dotNumber?minGapDays=N
 *
 * Uses the shared axios API service (JWT injection, 401 redirect, error
 * handling) — never raw fetch().
 *
 * The backend envelope is `{ success, data }`; the axios response interceptor
 * in api.ts unwraps it, so `response.data` IS the timeline result object.
 * There is no `total` in these envelopes, so no pagination re-wrap applies.
 */

import api from './api';
import type {
  AuthorityTimelineResult,
  InsuranceHistoryQueryParams,
  InsuranceTimelineResult,
} from '../types/carrierHistory';

/**
 * Get a carrier's reconstructed operating-authority timeline (grant,
 * application-pending, revocation-pending, revocation, suspension and
 * reinstatement spans), chronological, oldest first.
 */
export async function getCarrierAuthorityHistory(
  dotNumber: number
): Promise<AuthorityTimelineResult> {
  const response = await api.get<AuthorityTimelineResult>(
    `/carrier-history/authority/${dotNumber}`
  );
  return response.data;
}

/**
 * Get a carrier's reconstructed insurance timeline: one span per policy
 * period (underwriter, policy number, coverage amount, effective and
 * cancellation dates) plus the uncovered gaps between periods.
 */
export async function getCarrierInsuranceHistory(
  dotNumber: number,
  params?: InsuranceHistoryQueryParams
): Promise<InsuranceTimelineResult> {
  const response = await api.get<InsuranceTimelineResult>(
    `/carrier-history/insurance/${dotNumber}`,
    { params }
  );
  return response.data;
}

export const carrierHistoryApi = {
  getCarrierAuthorityHistory,
  getCarrierInsuranceHistory,
};

export default carrierHistoryApi;
